/**
 * URL redaction — strips credentials out of captured URLs before they leave
 * the browser.
 *
 * OAuth implicit-flow redirects park tokens in the fragment
 * (`#access_token=eyJ…&refresh_token=…&provider_token=ya29.…`). Those were
 * being captured verbatim as session, flag and telemetry URLs. A refresh token
 * does not expire on its own, so a stored one is a live credential.
 *
 * Every URL the tracker sends goes through redactUrl(). Mirrored server-side
 * in supabase/functions/_shared/redact.ts, which re-runs this on ingest for
 * payloads queued by an older tracker build.
 */

export const REDACTED = "[redacted]";

/**
 * Param names that carry a secret whatever their value looks like. Matched as
 * substrings, so `oauth_token`, `x-api-key` and `refresh_token` are all caught.
 */
const SENSITIVE_SUBSTRINGS = [
  "token",
  "secret",
  "password",
  "passwd",
  "pwd",
  "apikey",
  "api_key",
  "auth",
  "credential",
  "signature",
  "jwt",
  "otp",
  "session",
];

/**
 * Short ambiguous names, matched exactly so ordinary params that merely
 * contain them (`country_code`, `sort_key`) keep their values.
 */
const SENSITIVE_EXACT = new Set(["code", "key", "sig", "sid", "pass", "hash"]);

/**
 * Attribution params the pipeline reads downstream (classifyChannel,
 * extractUtmParams). Click IDs are long and opaque, so they must bypass the
 * value-shape rule below or channel attribution breaks.
 */
const ALLOWED_KEYS = new Set([
  "gclid",
  "fbclid",
  "msclkid",
  "ttclid",
  "twclid",
  "li_fat_id",
  "gad_source",
  "gad_campaignid",
  "ref",
  "source",
]);

/** `eyJ…header.payload.` — a JSON Web Token in the wild. */
const JWT_SHAPE = /^ey[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\./;

/**
 * A long run of token charset only. Excludes `+` and `%` so encoded prose
 * (`?title=A+long+headline`) survives while opaque bearer tokens
 * (`ya29.a0AQvPyIO…`) do not.
 */
const OPAQUE_SHAPE = /^[A-Za-z0-9._~/=-]{40,}$/;

function isSensitiveKey(key: string): boolean {
  const k = key.toLowerCase();
  if (ALLOWED_KEYS.has(k)) return false;
  if (SENSITIVE_EXACT.has(k)) return true;
  return SENSITIVE_SUBSTRINGS.some((s) => k.includes(s));
}

function isSensitiveValue(key: string, value: string): boolean {
  if (ALLOWED_KEYS.has(key.toLowerCase())) return false;
  if (value.startsWith("utm_")) return false;
  return JWT_SHAPE.test(value) || OPAQUE_SHAPE.test(value);
}

/**
 * Redact an `a=1&b=2` string, preserving key order and the keys themselves so
 * URLs still group and read sensibly in the UI.
 */
function redactParamString(params: string): string {
  return params
    .split("&")
    .map((pair) => {
      if (!pair) return pair;
      const eq = pair.indexOf("=");
      if (eq === -1) return pair;
      const key = pair.slice(0, eq);
      const value = pair.slice(eq + 1);
      if (!value) return pair;
      const decoded = safeDecode(value);
      if (isSensitiveKey(key) || isSensitiveValue(key, decoded)) {
        return `${key}=${REDACTED}`;
      }
      return pair;
    })
    .join("&");
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * A fragment is only treated as params when it actually looks like a param
 * string. Hash routes (`#/dashboard`) and anchors (`#pricing`) pass through
 * untouched; `#/settings?token=…` has only its query part redacted.
 */
function redactFragment(fragment: string): string {
  if (!fragment.includes("=")) return fragment;
  const q = fragment.indexOf("?");
  if (q !== -1) {
    return fragment.slice(0, q + 1) + redactParamString(fragment.slice(q + 1));
  }
  return redactParamString(fragment);
}

/**
 * Strip credentials from a URL's query string and fragment. Returns the input
 * unchanged when it cannot be parsed, and never throws — a redaction failure
 * must not break the host page.
 */
export function redactUrl(url: string | null | undefined): string {
  if (!url) return "";
  try {
    const hashIndex = url.indexOf("#");
    const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
    const fragment = hashIndex === -1 ? "" : url.slice(hashIndex + 1);

    const queryIndex = beforeHash.indexOf("?");
    const base = queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex);
    const query = queryIndex === -1 ? "" : beforeHash.slice(queryIndex + 1);

    let out = base;
    if (query) out += "?" + redactParamString(query);
    if (fragment) out += "#" + redactFragment(fragment);
    return out;
  } catch {
    // Unparseable input: drop everything after the origin rather than risk
    // shipping a token we failed to find.
    const cut = url.search(/[?#]/);
    return cut === -1 ? url : url.slice(0, cut);
  }
}
